import { Box, Flex } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  Button,
  CopyIcon,
  LinkedInIcon,
  Tooltip,
  theme,
  useSnackbar,
} from 'ui-components';
import { Editable } from '~/components/Editable/Editable';
import { FEATURE_FLAGS } from '~/constants';
import { MEDIA_KIT_TEXTS } from '~/constants/mediaKitTexts';
import { navigateWithTransition } from '~/core/utils/viewTransition/navigateWithTransition';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { useUpdateMediaKit } from '~/hooks/api/media-kits/update';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useRouteHistory } from '~/store/routeHistory.store';

import { useCopyMKLink } from './hooks/useCopyMKLink';
import { useDeleteMediaKitStore } from './modals/DeleteConfirmation/store';

export const Topbar = () => {
  const router = useRouter();
  const snackbar = useSnackbar();
  const { isEnabled: isShareToLinkedInEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsShareToLinkedIn,
  );
  const id = router.query.id as string;
  const { history } = useRouteHistory();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const mediaKit = useGetMediaKit({ id });
  const updateMediaKit = useUpdateMediaKit({ id });
  const { copyMkLink } = useCopyMKLink(id);
  const { openModalToDeleteMK } = useDeleteMediaKitStore();

  const [mediaKitName, setMediaKitName] = useState(mediaKit.data?.name || '');
  const [isSaved, setIsSaved] = useState(false);

  const { isEnabled: isPrimaryMediaKitEnabled } = useFeature(
    FEATURE_FLAGS.foamPrimaryMediaKit,
  );

  const tooltipText = useMemo(() => {
    if (!mediaKit.data?.is_public) return undefined;
    return isPrimaryMediaKitEnabled
      ? MEDIA_KIT_TEXTS.PRIMARY_DELETE
      : MEDIA_KIT_TEXTS.DEFAULT_DELETE;
  }, [mediaKit.data?.is_public, isPrimaryMediaKitEnabled]);

  useEffect(() => {
    if (mediaKit.data?.name) {
      setMediaKitName(mediaKit.data.name);
    }
  }, [mediaKit.data?.name]);

  useEffect(() => {
    if (isSaved) {
      saveTimeoutRef.current = setTimeout(() => {
        setIsSaved(false);
      }, 3000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isSaved]);

  const handleBackNavigation = useCallback(() => {
    if (history.length < 2) {
      navigateWithTransition(router, '/media-kits');
      return;
    }

    const previousPage = history[history.length - 2];
    const currentPage = history[history.length - 1];

    if (previousPage && previousPage !== currentPage) {
      navigateWithTransition(router, previousPage);
    } else {
      navigateWithTransition(router, '/media-kits');
    }
  }, [history, router]);

  const handleSave = useCallback(
    (value: string) => {
      if (!value || value.length < 3) {
        snackbar.error({
          description: 'Media kit name should be at least 3 characters long',
        });
        setMediaKitName(mediaKit.data?.name || '');
        return;
      }

      updateMediaKit.mutate(
        { name: value },
        {
          onSuccess: () => setIsSaved(true),
          onError: () => {
            snackbar.error({
              description: 'Failed to save media kit name. Please try again.',
            });
            setMediaKitName(mediaKit.data?.name || '');
          },
        },
      );
    },
    [mediaKit.data?.name, snackbar, updateMediaKit],
  );

  const handleShareToLinkedIn = useCallback(() => {
    const shareUrl = `${window.location.origin}/shared/media-kits/${id}?utm_source=linkedin`;
    const title = mediaKitName;
    const summary = `Check out ${title} on Foam 😎`;

    const linkedInUrl = `https://www.linkedin.com/shareArticle/?url=${encodeURIComponent(
      shareUrl,
    )}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(
      summary,
    )}&text=${encodeURIComponent(summary)}&mini=true`;

    // Open as popup window
    const width = 550;
    const height = 450;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      linkedInUrl,
      'Share to LinkedIn',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`,
    );
  }, [id, mediaKitName]);

  return (
    <Box
      backgroundColor="white"
      padding="32px 24px 24px 24px"
      borderBottom={`1px solid ${theme.colors.primary[100]}`}
    >
      <Flex flexWrap="wrap" justify="space-between">
        <Flex
          flex={1}
          maxWidth="80%"
          alignItems="center"
          justifyItems="center"
          gap={4}
        >
          <Button
            size="sm"
            variant="ghost"
            onClick={handleBackNavigation}
            aria-label="Go back"
            trackId="media-kit-builder-topbar-back-btn"
          >
            <ArrowLeftIcon boxSize={6} />
          </Button>
          {mediaKit.data?.name ? (
            <Editable
              defaultValue={mediaKit.data.name}
              isDisabled={mediaKit.isLoading || updateMediaKit.isLoading}
              onChange={setMediaKitName}
              onSubmit={handleSave}
              trackId="media-kit-name-editor"
              value={mediaKitName}
              isSaved={isSaved}
            />
          ) : null}
        </Flex>
        <Flex alignItems="center" justifyItems="center" gap={2}>
          {isShareToLinkedInEnabled ? (
            <Button
              size="md"
              variant="secondary"
              leftIcon={<LinkedInIcon />}
              onClick={handleShareToLinkedIn}
              trackId="media-kit-builder-topbar--linkedin-share-btn"
              sx={{
                backgroundColor: '#0273b1',
                '&:hover': {
                  backgroundColor: '#0273c1',
                },
              }}
            >
              Share to LinkedIn
            </Button>
          ) : (
            <Tooltip label={tooltipText} placement="top">
              <span>
                <Button
                  size="md"
                  variant="tertiary"
                  onClick={() => openModalToDeleteMK(id)}
                  trackId="media-kit-builder-topbar--delete-btn"
                  isDisabled={mediaKit.data?.is_public}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          )}

          <Button
            size="md"
            variant="primary"
            leftIcon={<CopyIcon />}
            onClick={copyMkLink}
            trackId="media-kit-builder-topbar--copy-link-btn"
          >
            Copy link
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
